// SeatBoard — the middle "seats board" of the play surface. For 1..4 players it renders an ORIENTED
// 3×3 CSS-grid table (you upright at the bottom; clockwise the others fill right/top/left, rotated
// 270/180/90°) so "whose river is this" reads instantly like a real table. For 5..6 players it keeps
// the schematic VERTICAL STACK (no real pentagon/hexagon — rotation does not generalize). A manual
// `forceList` toggle forces the stack even at <=4 players (owner preference).
//
// Rotation footprint (correctness): CSS `transform: rotate()` does NOT change the layout box, so a
// rotated river would reserve its UN-rotated size and overlap neighbours. Every rotated zone is wrapped
// in an outer box whose width/height are the SWAPPED inner dimensions (90/270 → outerW = innerH) and the
// inner block is centered + rotated via translate(-50%,-50%) rotate(deg). The grid `auto` tracks then
// reserve the right space and seats never overlap. Pure CSS transforms (no PixiJS); TileView is untouched
// (rotation lives in the wrapping spans, mirroring the existing riichi-tile / MeldView pattern).
//
// Styling (P2 front, 設計書 §9): colors come from theme.css tokens/classes — .center-plate (漆黒+金縁、
// 場況・ドラの唯一の正出所 D16) / .seat-plate ピル / .seat-wind / .riichi-stick / .river-tile / .num.
// The fx props (riverDelays / latestDiscard / deltas / turnSeat) are ALL optional — omitted ⇒ current
// behaviour, so callers compile & render unchanged until the integrate track wires useTableFx in.
import { type CSSProperties, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import type { Meld, Seat, TileId } from '@mj/protocol';
import { TileView } from './TileView';
import { MeldView } from './MeldView';
import { TileFan } from './TileFan';
import { fitRiver, useElementSize, RIVER_COLS_DEFAULT } from '../ui/fit';

const WIND: Record<string, string> = { z1: '東', z2: '南', z3: '西', z4: '北' };

// D3 白リング (latest discard) と立直宣言牌の金細線 (gold-400 derived, D4)。立直牌は inline box-shadow を
// 持つため、theme.css の .river-tile[data-last="1"] リングと同居できるよう両方をここで合成する
// (inline box-shadow がクラス側を上書きしてリングが消える事故の予防)。
const LAST_RING = '0 0 0 2px rgba(255,255,255,0.85), 0 0 10px 2px rgba(255,255,255,0.35)';
const RIICHI_LINE = '0 2px 0 rgba(232,193,74,0.5)';

// The per-player shape this board reads (a structural subset of the engine PlayerState; kept local so
// the board has no engine dependency and stays a pure render component).
export interface SeatView {
  seatWind?: TileId | null;
  score: number;
  hand: TileId[];
  melds: Meld[];
  discards: { tile: TileId; tsumogiri: boolean; riichiTile: boolean; calledAway: boolean }[];
  riichi?: { declared?: boolean; open?: boolean; infinite?: boolean } | null;
  kitaCount?: number;
  flowers?: TileId[];
  /** true while this seat's socket has dropped but it is still inside the reconnect grace (badge phase). */
  offline?: boolean;
}

export interface SeatBoardProps {
  players: SeatView[];
  me: Seat;
  dealer: Seat;
  playerCount: number;
  oriented: boolean; // false ⇒ force the vertical stack even at <=4 players
  seatLabel: (seat: number) => string;
  cpuTargetName: (seat: number) => string | null;
  // center cell (oriented mode): round/honba/wall/dora summary — built by the caller from header data.
  center: ReactNode;
  // ---- fx props（設計書 §9。すべて optional、未指定で現挙動）----
  /** `${seat}:${discardIndex}` → animation-delay ms。該当河牌に fx-land-sm を付け時間差着地させる。 */
  riverDelays?: Record<string, number>;
  /** 最新打牌 → data-last="1"（theme.css 側で白リング、D3）。 */
  latestDiscard?: { seat: number; index: number } | null;
  /** 点数デルタ float ラベル（+: --c-gain / −: --c-loss、D5）。key は id。 */
  deltas?: { seat: number; amount: number; id: number }[];
  /** 手番席 → 席プレートに fx-turn パルス。 */
  turnSeat?: number | null;
}

// Internal bundle so the optional fx props thread through one parameter (exactOptionalPropertyTypes:
// fields are required-but-undefined so plain pass-through assignment typechecks).
interface FxProps {
  riverDelays: Record<string, number> | undefined;
  latestDiscard: { seat: number; index: number } | null | undefined;
  deltas: { seat: number; amount: number; id: number }[] | undefined;
  turnSeat: number | null | undefined;
}

const TILE = {
  // opponent river tiles stay small for a 390px phone; your own river a touch larger; melds smaller.
  // *Min は河の下限 (fitRiver minW): 河が無限に増えても扇/縮小でここまでは縮め、決して牌を消さない。
  oppRiver: 14, meRiver: 17, oppMeld: 13, meMeld: 15, oppRiverMin: 6, meRiverMin: 7,
} as const;

// 花/北抜き PILE 扇 (TileFan) の box 上限 (tileSize × この列数)。これを超える分は扇で重ね、×N を併記
// する → strip が横へ無限に広がらず (no-wrap・空間有界)、かつ牌は1枚も消えない。
const PILE_FAN_COLS = 7;

// Per-cell fx lookup shared by the oriented river and the stack fallback river.
function riverCellFx(seat: number, di: number, fx: FxProps): { delay: number | undefined; isLast: boolean; cls: string } {
  const delay = fx.riverDelays?.[`${seat}:${di}`];
  const isLast = fx.latestDiscard != null && fx.latestDiscard.seat === seat && fx.latestDiscard.index === di;
  // バッチ内の他家打牌は fx-land-sm + delay で逐次感（A2）。delay の無い最新打牌のみ fx-land（A1）。
  const cls = `river-tile${delay != null ? ' fx-land-sm' : isLast ? ' fx-land' : ''}`;
  return { delay, isLast, cls };
}

// A player's discard river — P2 lossless fit (設計書 §5)。河は無限に増えうる (花/北抜き連発でも) ので
// 18枚 cap は撤廃し、**全 discards を必ず描く**。fitRiver で与えられた box から牌寸法と行送り
// (rowPitch) を算出: 横は cols 固定 (6) で tileW を box 幅から、縦は rows を rowPitch で boxH に
// 押し込む。行が縦に重なる時 (vOverlap) は新しい行ほど zIndex を大きくして最新打牌を上に出す
// (情報は1枚も消えず、上端だけが重なる lossless)。`natW` は広い時に牌が育ちすぎない上限 (席ごとの目標寸法)。
//
// box は **呼び出し側 (SeatBoard) が table root を計測して各席へ配る予算**。SeatRiver 自身は box に
// 対して content-sized に解くだけ (自分で 100% を主張しない) ので、回転卓の OrientedZone は従来どおり
// content の offset 寸法を測って swap footprint を確保できる (循環依存なし)。box 未指定 (後方互換) や
// 未計測 (0) のうちは natW ベースの自然寸法に倒れ、牌は1枚も消えない。回転は OrientedZone (等長変換)
// の責務なので、SeatRiver は未回転 (inner) 座標系の box に対してだけ解けばよい。
function SeatRiver({
  discards,
  natW,
  minW,
  seat,
  fx,
  boxW = 0,
  boxH = 0,
  cols = RIVER_COLS_DEFAULT,
}: {
  discards: SeatView['discards'];
  natW: number; // 広い時の牌幅上限 (席ごとの目標寸法 = 旧 size)
  minW: number; // 牌幅下限 (これ未満には縮めない最終下限)
  seat: number;
  fx: FxProps;
  boxW?: number; // 河に与えられた box 幅 (px)。未指定/0 で natW ベースにフォールバック (後方互換)
  boxH?: number; // 河に与えられた box 高 (px)。未指定/0 で全行 tileH で並べる (フォールバック)
  cols?: number; // 列数 (既定 RIVER_COLS_DEFAULT=6)
}) {
  // box 幅から tileW 候補を求め、natW を上限に clamp (広い時に育ちすぎない)。box 未計測なら natW。
  const fit = fitRiver({ boxW, boxH, count: discards.length, cols, minW });
  const tileW = Math.max(minW, Math.min(Math.round(fit.tileW), natW));
  const tileH = Math.round(tileW * 1.36);
  // tileW を natW へ clamp した分 rowPitch も牌高基準で再計算 (boxH があれば必ずその中へ収める)。
  const rowPitch =
    boxH > 0 ? Math.max(2, Math.min(tileH + 2, Math.floor(boxH / fit.rows))) : tileH + 2;
  const vOverlap = rowPitch < tileH + 2 - 0.5;
  const cellW = tileW + 2;
  const riichiCellW = tileH + 4; // 横倒し牌は tileH 分の幅を占める
  const gridW = cellW * cols + (cols - 1) * 2; // content の実幅 (OrientedZone がこれを測る)
  return (
    <div
      className="river-grid"
      style={{
        gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
        gridAutoRows: `${rowPitch}px`,
        gap: '0 2px', // 行ギャップは rowPitch に内包させるため 0、列ギャップのみ 2px
        justifyContent: 'start',
        alignContent: 'start',
        // 行 track (rowPitch) より牌が高い時 (vOverlap) はセルを start 揃えで上端固定し、はみ出しを
        // 下へ重ねる (stretch だと牌が track 高に潰れて lossless でなくなる)。overflow:visible は class 側。
        alignItems: 'start',
        width: gridW,
        minWidth: gridW,
      }}
    >
      {discards.map((d, di) => {
        const { delay, isLast, cls } = riverCellFx(seat, di, fx);
        const rowIndex = Math.floor(di / cols);
        // 縦オーバーラップ時は後の行ほど上 (最新打牌を見せる)。重ならない時は zIndex 不要 (auto)。
        const zIndex = vOverlap ? rowIndex + 1 : undefined;
        const common: CSSProperties = {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 3,
          background: d.tsumogiri ? 'var(--c-tsumogiri)' : undefined,
          opacity: d.calledAway ? 0.35 : 1,
          animationDelay: delay != null ? `${delay}ms` : undefined,
          zIndex,
        };
        return d.riichiTile ? (
          <span
            key={di}
            className={cls}
            data-last={isLast ? '1' : undefined}
            title="立直宣言牌"
            style={{
              ...common,
              gridColumn: 'span 1',
              width: riichiCellW,
              height: tileH + 2,
              boxShadow: isLast ? `${LAST_RING}, ${RIICHI_LINE}` : RIICHI_LINE,
            }}
          >
            <span style={{ display: 'inline-flex', transform: 'rotate(90deg)' }}>
              <TileView tile={d.tile} size={tileW} />
            </span>
          </span>
        ) : (
          <span
            key={di}
            className={cls}
            data-last={isLast ? '1' : undefined}
            title={d.tsumogiri ? 'ツモ切り' : undefined}
            style={common}
          >
            <TileView tile={d.tile} size={tileW} />
          </span>
        );
      })}
    </div>
  );
}

// A player's called area: 副露 (MeldView) + 北抜き + 花牌, left→right. (Mirror of SoloMahjong's MeldArea.)
function MeldStrip({ melds, kitaCount, flowers, seat, players, size }: {
  melds: Meld[]; kitaCount: number; flowers: TileId[]; seat: Seat; players: number; size: number;
}) {
  if (melds.length === 0 && kitaCount === 0 && flowers.length === 0) return null;
  // 北抜き/花牌は無制限に増えうる PILE → TileFan で扇 (下限寸法でも溢れる時のみ重ね、×N を併記)。box は
  // 固定上限 (size×PILE_FAN_COLS) に bound して strip が無限に広がらないようにする (no-wrap・空間有界)。
  // 牌は1枚も消えない (扇 + ×N 併記)。副露 (MeldView) は手構成で有界なので nowrap で素直に並べる。
  const fanBoxW = size * PILE_FAN_COLS;
  const kitaTiles: TileId[] = kitaCount > 0 ? Array.from({ length: kitaCount }, () => 'z4' as TileId) : [];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexWrap: 'nowrap', minWidth: 0 }}>
      {melds.map((m, mi) => <MeldView key={mi} meld={m} ownerSeat={seat} players={players} size={size} />)}
      {kitaCount > 0 && (
        <TileFan tiles={kitaTiles} boxW={fanBoxW} tileSize={size} title={`北抜き ×${kitaCount}`} />
      )}
      {flowers.length > 0 && (
        <TileFan tiles={flowers} boxW={fanBoxW} tileSize={size} title="花牌（ドラ）" />
      )}
    </div>
  );
}

// 他家の「手牌」を雀魂式に表す伏せ牌帯。実手牌は秘匿 (枚数のみ既知) なので裏牌の扇で代替し、副露の
// 左隣 (= 手牌の右に副露) に置く。lossless 不変条件は副露/河に課されるもので、伏せ手牌は元から情報を
// 持たない (枚数は ×N で保持) ため扇化は規約に抵触しない。自分 (seat===me) はトレイ側 (SoloMahjong の
// MeldArea) が手牌を描くので二重描画を避けて null を返す。
function HandProxy({ seat, me, count, size }: { seat: Seat; me: Seat; count: number; size: number }) {
  if (seat === me || count <= 0) return null;
  // 裏牌は識別子を持たないが TileFan は key/枚数に tiles を使うので適当な z4 で埋める (faceDown で表は出ない)。
  const backs: TileId[] = Array.from({ length: count }, () => 'z4' as TileId);
  return (
    <TileFan tiles={backs} boxW={size * PILE_FAN_COLS} tileSize={size} faceDown title={`手牌 ${count}枚`} />
  );
}

// Score delta labels floated above a seat plate (A10): + = --c-gain / − = --c-loss (D5), keyed by id so
// a new payment re-mounts (and so re-plays fx-delta-float) even if the amount repeats.
// NOTE: positioned via left/right + textAlign, NOT translateX — the keyframe animates `transform` and
// would stomp an inline translate.
function DeltaLabels({ deltas, seat }: { deltas: FxProps['deltas']; seat: number }) {
  const mine = (deltas ?? []).filter((d) => d.seat === seat);
  if (mine.length === 0) return null;
  return (
    <>
      {mine.map((d, i) => (
        <span
          key={d.id}
          className="num fx-delta-float"
          style={{
            position: 'absolute', left: 0, right: 0, bottom: `calc(100% + ${2 + i * 14}px)`,
            textAlign: 'center', fontWeight: 700, fontSize: 13, pointerEvents: 'none',
            color: d.amount >= 0 ? 'var(--c-gain)' : 'var(--c-loss)',
          }}
        >
          {d.amount >= 0 ? `+${d.amount.toLocaleString()}` : d.amount.toLocaleString()}
        </span>
      ))}
    </>
  );
}

// The name/score/wind nameplate shown for each seat in the oriented table — a .seat-plate pill:
// wind circle (親 = grad-gold + グロー、●親テキストの代替) / name / .num score / riichi label + white stick.
// 手牌枚数 (旧 `手N枚`) は HandProxy の伏せ牌帯へ一本化したので nameplate からは出さない (重複排除)。
function NamePlate({ name, seatWind, score, isDealer, riichi, target, compact, seat, fx, offline }: {
  name: string; seatWind: string; score: number; isDealer: boolean;
  riichi: SeatView['riichi']; target: string | null; compact: boolean;
  seat: number; fx: FxProps; offline?: boolean;
}) {
  const wind = WIND[seatWind] ?? '';
  const riichiText = riichi?.declared ? (riichi.infinite ? '無限立直' : riichi.open ? 'オープン立直' : '立直') : null;
  const turn = fx.turnSeat != null && fx.turnSeat === seat;
  return (
    <div
      className={`seat-plate${turn ? ' fx-turn' : ''}`}
      data-dealer={isDealer ? '1' : undefined}
      style={{
        // position:relative anchors the delta labels; the pill look itself lives in theme.css.
        position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6,
        whiteSpace: 'nowrap', fontSize: compact ? 11 : 12, lineHeight: 1.25,
      }}
    >
      <DeltaLabels deltas={fx.deltas} seat={seat} />
      {wind !== '' && (
        <span
          className="seat-wind"
          // 親 = grad-gold + グロー (§9)。dealer variant が theme.css に無くても成立するよう inline で。
          style={isDealer ? { background: 'var(--grad-gold)', color: 'var(--gold-ink)', boxShadow: '0 0 6px rgba(232,193,74,0.65)' } : undefined}
        >
          {wind}
        </span>
      )}
      <strong>{name}</strong>
      {isDealer && wind === '' && <span style={{ color: 'var(--gold-400)' }}>●親</span>}
      <span className="seat-score num" style={{ fontWeight: 700 }}>{score.toLocaleString()}</span>
      {riichiText && (
        <span className="seat-riichi-label" style={{ color: 'var(--c-riichi)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          {riichiText}
          <span className="riichi-stick" aria-hidden="true" />
        </span>
      )}
      {target && <span style={{ color: 'var(--gold-400)', fontSize: 10 }}>🎯{target}</span>}
      {offline && (
        <span style={{ color: 'var(--c-danger, #e5484d)', fontSize: 10, whiteSpace: 'nowrap' }}>再接続中…</span>
      )}
    </div>
  );
}

// One opponent zone (nameplate + melds + river), rotated by `deg` (0/90/180/270). The zone measures its
// natural (un-rotated) size, then the OUTER wrapper reserves the SWAPPED footprint so the grid track is
// sized correctly and the rotated content never overlaps its neighbours.
function OrientedZone({ deg, children }: { deg: 0 | 90 | 180 | 270; children: ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [dim, setDim] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => setDim({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // observe once on mount; the ResizeObserver keeps dim current on content/size changes
  const swap = deg === 90 || deg === 270;
  const outerW = swap ? dim.h : dim.w;
  const outerH = swap ? dim.w : dim.h;
  return (
    <div style={{ position: 'relative', width: outerW || undefined, height: outerH || undefined }}>
      <div
        ref={innerRef}
        style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: `translate(-50%,-50%) rotate(${deg}deg)`, transformOrigin: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// The contents of one seat (nameplate over melds over river). Used inside OrientedZone (others) and
// upright for the bottom (you) zone. `riverBox` は呼び出し側が table root から配る河の box 予算
// (未回転=inner 座標系)。0 のときは natW の自然寸法で描く (計測前/フォールバック)。
function SeatContent({ p, seat, me, dealer, players, seatLabel, cpuTargetName, riverSize, riverMinW, riverBox, meldSize, fx }: {
  p: SeatView; seat: number; me: Seat; dealer: Seat; players: number;
  seatLabel: (seat: number) => string; cpuTargetName: (seat: number) => string | null;
  riverSize: number; riverMinW: number; riverBox: { w: number; h: number }; meldSize: number; fx: FxProps;
}) {
  // 雀魂式 2 段レイアウト: [ネーム] / [手牌|副露 を 1 本の横ライン] / [河 単独レーン]。副露を河の上段から
  // 外し「手牌の右隣」へ移すことで、回転後は副露が常に各席の外周側・河が中心側に並び、副露が center-plate
  // へ突き出さない (根因A/B/C 解消)。自分 (bottom) は手牌/副露をトレイ側が描くので行1は空 (二重描画回避)。
  const handMeldRow =
    seat !== me ? (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexWrap: 'nowrap', minWidth: 0 }}>
        <HandProxy seat={seat as Seat} me={me} count={p.hand.length} size={meldSize} />
        <MeldStrip melds={p.melds} kitaCount={p.kitaCount ?? 0} flowers={p.flowers ?? []} seat={seat as Seat} players={players} size={meldSize} />
      </div>
    ) : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      <NamePlate
        name={seatLabel(seat)} seatWind={p.seatWind ?? ''} score={p.score}
        isDealer={seat === dealer} riichi={p.riichi ?? null}
        target={cpuTargetName(seat)} compact={seat !== me}
        seat={seat} fx={fx} {...(p.offline !== undefined ? { offline: p.offline } : {})}
      />
      {handMeldRow}
      <SeatRiver
        discards={p.discards} natW={riverSize} minW={riverMinW}
        boxW={riverBox.w} boxH={riverBox.h} seat={seat} fx={fx}
      />
    </div>
  );
}

// Stack-mode river — self-measuring (the river column is flex:1 1 auto inside its row, so its box is
// bounded by the row; no rotation ⇒ no circular dependency, unlike OrientedZone). fitRiver で行内 box に
// 全 discards を縦オーバーラップで収める (cap なし・切り捨てなし)。最新行ほど zIndex を上げる。
function StackRiver({ discards, seat, fx }: { discards: SeatView['discards']; seat: number; fx: FxProps }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const box = useElementSize(rootRef);
  const natW = 18; // 旧 stack river の固定寸法を上限 (目標) に
  const minW = 7;
  const fit = fitRiver({ boxW: box.w, boxH: box.h, count: discards.length, cols: RIVER_COLS_DEFAULT, minW });
  const tileW = Math.max(minW, Math.min(Math.round(fit.tileW), natW));
  const tileH = Math.round(tileW * 1.36);
  const rowPitch =
    box.h > 0 ? Math.max(2, Math.min(tileH + 2, Math.floor(box.h / fit.rows))) : tileH + 2;
  const vOverlap = rowPitch < tileH + 2 - 0.5;
  const cellW = tileW + 2;
  const riichiCellW = tileH + 4;
  return (
    <div
      ref={rootRef}
      className="river-grid"
      style={{
        flex: '1 1 auto', minWidth: 0, alignSelf: 'stretch',
        gridTemplateColumns: `repeat(${RIVER_COLS_DEFAULT}, ${cellW}px)`,
        gridAutoRows: `${rowPitch}px`, gap: '0 2px', justifyContent: 'start', alignContent: 'start',
        alignItems: 'start', // vOverlap 時に牌を上端固定し下へ重ねる (track 高に潰さない)
      }}
    >
      {discards.map((d, di) => {
        const { delay, isLast, cls } = riverCellFx(seat, di, fx);
        const zIndex = vOverlap ? Math.floor(di / RIVER_COLS_DEFAULT) + 1 : undefined;
        const common: CSSProperties = {
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 3,
          background: d.tsumogiri ? 'var(--c-tsumogiri)' : undefined,
          opacity: d.calledAway ? 0.35 : 1,
          animationDelay: delay != null ? `${delay}ms` : undefined,
          zIndex,
        };
        return d.riichiTile ? (
          <span
            key={di} className={cls} data-last={isLast ? '1' : undefined} title="立直宣言牌"
            style={{ ...common, width: riichiCellW, height: tileH + 2, boxShadow: isLast ? `${LAST_RING}, ${RIICHI_LINE}` : RIICHI_LINE }}
          >
            <span style={{ display: 'inline-flex', transform: 'rotate(90deg)' }}><TileView tile={d.tile} size={tileW} /></span>
          </span>
        ) : (
          <span key={di} className={cls} data-last={isLast ? '1' : undefined} title={d.tsumogiri ? 'ツモ切り' : undefined} style={common}>
            <TileView tile={d.tile} size={tileW} />
          </span>
        );
      })}
    </div>
  );
}

// Vertical stack (the original board): used for 5..6 players, and for <=4 when forceList is on.
// P2: 河は cap なしで全牌を box 内へ fit (StackRiver)。各行は 1fr で table 高さを等分 → 5〜6人でも
// 卓はスクロールしない (overflowY:auto を撤廃)。色は theme.css トークン、fx フック (river-tile /
// data-last / delays / fx-turn / deltas) はそのまま。
function StackBoard({ players, me, dealer, seatLabel, cpuTargetName, fx }: {
  players: SeatView[]; me: Seat; dealer: Seat;
  seatLabel: (seat: number) => string; cpuTargetName: (seat: number) => string | null; fx: FxProps;
}) {
  const n = players.length;
  return (
    <div style={{ height: '100%', overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr', gridAutoRows: '1fr', gap: 6 }}>
      {players.map((p, seat) => {
        const turn = fx.turnSeat != null && fx.turnSeat === seat;
        return (
          <div
            key={seat}
            style={{
              display: 'flex', gap: 8, alignItems: 'stretch', padding: 6, borderRadius: 8,
              minHeight: 0, overflow: 'hidden', // 行は 1fr で高さ有界 → 河はこの中に fit (スクロールしない)
              background: seat === me ? 'var(--surface-panel)' : 'var(--surface-card)',
              border: `1px solid ${seat === me ? 'var(--border-gold)' : 'var(--border-panel)'}`,
              color: 'var(--text-hi)',
            }}
          >
            <div className={turn ? 'fx-turn' : undefined} style={{ position: 'relative', minWidth: 110, fontSize: 13 }}>
              <DeltaLabels deltas={fx.deltas} seat={seat} />
              <strong>{seatLabel(seat)}</strong> {WIND[p.seatWind ?? ''] ?? ''}
              {cpuTargetName(seat) && <span style={{ color: 'var(--gold-400)', fontSize: 11 }}> 🎯{cpuTargetName(seat)}</span>}
              <br />
              <span className="num" style={{ color: 'var(--text-mid)' }}>{p.score.toLocaleString()}点</span>
              {seat === dealer && <span style={{ color: 'var(--gold-400)' }}> ●親</span>}
              {p.riichi?.declared && (
                <span style={{ color: 'var(--c-riichi)' }}>
                  {' '}{p.riichi.infinite ? '無限立直' : p.riichi.open ? 'オープン立直' : '立直'}
                  <span className="riichi-stick" aria-hidden="true" style={{ marginLeft: 3, verticalAlign: 'middle' }} />
                </span>
              )}
              {p.offline && (
                <span style={{ color: 'var(--c-danger, #e5484d)', fontSize: 10, whiteSpace: 'nowrap' }}> 再接続中…</span>
              )}
            </div>
            {/* 手牌|副露ユニット: 雀魂式に「手牌(伏せ) の右へ副露」を 1 本の横ラインで。河とは縦区切り
                (borderLeft) で明示分離し「誰のものか/どこからが河か」を明瞭にする (根因D 解消)。
                自分 (me) はトレイ側が手牌/副露を描くので出さない (二重描画回避)。 */}
            {seat !== me && (p.hand.length > 0 || p.melds.length > 0 || (p.kitaCount ?? 0) > 0 || (p.flowers ?? []).length > 0) && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexWrap: 'nowrap', flex: '0 0 auto', minWidth: 0, paddingRight: 8, borderRight: '1px solid var(--border-panel)' }}>
                <HandProxy seat={seat as Seat} me={me} count={p.hand.length} size={15} />
                <MeldStrip melds={p.melds} kitaCount={p.kitaCount ?? 0} flowers={p.flowers ?? []} seat={seat as Seat} players={n} size={15} />
              </div>
            )}
            <StackRiver discards={p.discards} seat={seat} fx={fx} />
          </div>
        );
      })}
    </div>
  );
}

// Oriented 3×3 table for 1..4 players. Seat→zone mapping: pos = (seat - me + n) % n.
// 0→bottom(upright) / 1→right(rot270) / 2→top(rot180) / 3→left(rot90) — clockwise around you.
function OrientedTable({ players, me, dealer, seatLabel, cpuTargetName, center, fx }: {
  players: SeatView[]; me: Seat; dealer: Seat;
  seatLabel: (seat: number) => string; cpuTargetName: (seat: number) => string | null; center: ReactNode;
  fx: FxProps;
}) {
  const n = players.length;
  // resolve which actual seat sits in each clockwise position (or -1 if that position is unoccupied).
  const seatAtPos = (pos: number): number => {
    for (let s = 0; s < n; s++) if (((s - me + n) % n) === pos) return s;
    return -1;
  };
  const bottom = seatAtPos(0); // always me
  const right = seatAtPos(1);
  const top = seatAtPos(2);
  const left = seatAtPos(3);

  // table root を計測し、各席の河 box 予算 (未回転=inner 座標系) を配る。河は無限に増えても box 内へ
  // (fitRiver: rows*rowPitch<=boxH) 縦オーバーラップで収まり、切り捨てない。予算は table を超えない:
  // 縦は top+center+bottom、横は left+center+right で分け合うので各辺へ 0.30〜0.34 ずつ割く。
  // boxW は cols 固定ゆえ横圧縮はほぼ不要 → natW の 6列幅を渡し横は牌を育てすぎない上限とする。
  // (回転は OrientedZone の等長変換に委ね、ここでは未回転の inner box だけ算出する。)
  const tableRef = useRef<HTMLDivElement>(null);
  const table = useElementSize(tableRef);
  const oppBoxW = TILE.oppRiver * RIVER_COLS_DEFAULT + RIVER_COLS_DEFAULT * 2;
  const meBoxW = TILE.meRiver * RIVER_COLS_DEFAULT + RIVER_COLS_DEFAULT * 2;
  // 各席ゾーン (top/bottom) は [nameplate] + [手牌|副露] + [河] の縦積み。河に渡す boxH は **ゾーン全体の
  // 予算 vBudget から nameplate/副露の実高 (NAMEPLATE_RESERVE) を引いた残り** にする。こうしないと
  // 河は vBudget まるごとを予約し、その上に nameplate が乗って「ゾーン高 = vBudget + nameplate」へ膨らみ、
  // grid の auto 行が container (height:100%) を超えてオーバーフロー → 下端の自分の河が dock 帯に
  // 隠れる (要修正の根因)。reserve を引けば nameplate + 河 ≤ vBudget に収まり、top+bottom ≤ 0.68h で
  // center (minmax(0,1fr)) と合わせて container 内に必ず収まる。
  const NAMEPLATE_RESERVE = 30; // .seat-plate(~24px) + SeatContent gap(3px×2) の実測上限
  // 縦持ち牌 (bottom/top): inner の縦 = table 縦の一部。横倒し席 (left/right): inner の縦 = table 横の一部。
  const vBudget = table.h > 0 ? Math.max(0, table.h * 0.34) : 0;
  const hBudget = table.w > 0 ? Math.max(0, table.w * 0.30) : 0;
  const vRiverH = vBudget > 0 ? Math.max(0, vBudget - NAMEPLATE_RESERVE) : 0;
  const hRiverH = hBudget > 0 ? Math.max(0, hBudget - NAMEPLATE_RESERVE) : 0;
  const meBox = { w: meBoxW, h: vRiverH };
  const topBox = { w: oppBoxW, h: vRiverH };
  const sideBox = { w: oppBoxW, h: hRiverH };

  const opp = (seat: number, deg: 0 | 90 | 180 | 270, riverBox: { w: number; h: number }) =>
    seat < 0 ? null : (
      <OrientedZone deg={deg}>
        <SeatContent
          p={players[seat]!} seat={seat} me={me} dealer={dealer} players={n}
          seatLabel={seatLabel} cpuTargetName={cpuTargetName}
          riverSize={TILE.oppRiver} riverMinW={TILE.oppRiverMin} riverBox={riverBox}
          meldSize={TILE.oppMeld} fx={fx}
        />
      </OrientedZone>
    );

  return (
    <div
      ref={tableRef}
      style={{
        height: '100%', display: 'grid', gap: 4, overflow: 'hidden',
        // minmax(0,…): 周辺トラックは content を割ってでも 0 まで縮みうる、中央は 0 まで譲りうる。
        // これで auto 行の content (nameplate+河) が予算を超えても grid が container (height:100%) を
        // 突き抜けず、下端ゾーン (自分の河) が dock 帯へオーバーフローして隠れることがない。
        gridTemplateColumns: 'minmax(0, auto) minmax(0, 1fr) minmax(0, auto)',
        gridTemplateRows: 'minmax(0, auto) minmax(0, 1fr) minmax(0, auto)',
        gridTemplateAreas: `'. top .' 'left center right' '. bottom .'`,
        justifyItems: 'center', alignItems: 'center',
      }}
    >
      <div style={{ gridArea: 'top', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>{opp(top, 180, topBox)}</div>
      <div style={{ gridArea: 'left', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>{opp(left, 90, sideBox)}</div>
      <div
        // 漆黒プレート + 金縁 (.center-plate)。場況・ドラの唯一の正出所 (D16) — 中身は caller が組む。
        className="center-plate"
        style={{
          gridArea: 'center', justifySelf: 'stretch', alignSelf: 'stretch',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 4,
          minWidth: 0, overflow: 'hidden',
        }}
      >
        {center}
      </div>
      <div style={{ gridArea: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{opp(right, 270, sideBox)}</div>
      <div style={{ gridArea: 'bottom', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
        {bottom >= 0 && (
          <SeatContent
            p={players[bottom]!} seat={bottom} me={me} dealer={dealer} players={n}
            seatLabel={seatLabel} cpuTargetName={cpuTargetName}
            riverSize={TILE.meRiver} riverMinW={TILE.meRiverMin} riverBox={meBox}
            meldSize={TILE.meMeld} fx={fx}
          />
        )}
      </div>
    </div>
  );
}

export function SeatBoard(props: SeatBoardProps) {
  const { players, me, dealer, playerCount, oriented, seatLabel, cpuTargetName, center } = props;
  const fx: FxProps = {
    riverDelays: props.riverDelays,
    latestDiscard: props.latestDiscard,
    deltas: props.deltas,
    turnSeat: props.turnSeat,
  };
  const useOriented = oriented && playerCount <= 4;
  if (useOriented) {
    return (
      <OrientedTable
        players={players} me={me} dealer={dealer}
        seatLabel={seatLabel} cpuTargetName={cpuTargetName} center={center} fx={fx}
      />
    );
  }
  return <StackBoard players={players} me={me} dealer={dealer} seatLabel={seatLabel} cpuTargetName={cpuTargetName} fx={fx} />;
}
